import React, { useState, useEffect } from 'react';
import {
  FiBell as Bell,
  FiAlertTriangle as AlertTriangle,
  FiClock as Clock,
  FiCheckCircle as CheckCircle,
  FiX as X,
  FiPhone as Phone,
  FiMail as Mail,
  FiCalendar as Calendar,
  FiArrowRight as ArrowRight
} from 'react-icons/fi';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { currentPolicies } from '../utils/policyStatus';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL } from '../constants/contact';

const ExpirationAlertsWidget = ({ isCompact = false, onViewAll }) => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  useEffect(() => {
    if (user?.policies) {
      generateAlerts(user.policies);
    }
    
    // Load dismissed alerts from localStorage
    const dismissed = JSON.parse(localStorage.getItem('dismissedAlerts') || '[]');
    setDismissedAlerts(dismissed);
  }, [user]);

  const generateAlerts = (policies) => {
    const today = new Date();
    const generatedAlerts = [];

    policies.forEach(policy => {
      const expirationDate = new Date(policy.expirationDate);
      const daysUntilExpiry = differenceInDays(expirationDate, today);

      // Generate alerts for policies expiring within 90 days
      if (daysUntilExpiry >= 0 && daysUntilExpiry <= 90) {
        let severity = 'low';
        let title = '';
        let message = '';
        let actionRequired = false;

        if (daysUntilExpiry <= 7) {
          severity = 'critical';
          title = 'URGENT: Policy Expiring Very Soon!';
          message = `Your ${policy.policyType} expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}. Contact us immediately to avoid coverage gaps.`;
          actionRequired = true;
        } else if (daysUntilExpiry <= 14) {
          severity = 'high';
          title = 'Policy Expiring Soon';
          message = `Your ${policy.policyType} expires in ${daysUntilExpiry} days. Please contact us to renew your coverage.`;
          actionRequired = true;
        } else if (daysUntilExpiry <= 30) {
          severity = 'medium';
          title = 'Renewal Available';
          message = `Your ${policy.policyType} is up for renewal in ${daysUntilExpiry} days. Start planning your renewal now.`;
          actionRequired = false;
        } else {
          severity = 'low';
          title = 'Upcoming Renewal';
          message = `Your ${policy.policyType} will be available for renewal in ${daysUntilExpiry} days.`;
          actionRequired = false;
        }

        generatedAlerts.push({
          id: `alert_${policy.id}`,
          policyId: policy.id,
          policyNumber: policy.policyNumber,
          policyType: policy.policyType,
          vendor: policy.vendor,
          expirationDate: policy.expirationDate,
          daysUntilExpiry,
          severity,
          title,
          message,
          actionRequired,
          createdAt: new Date().toISOString()
        });
      }
    });

    // Sort by severity and days until expiry
    generatedAlerts.sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return a.daysUntilExpiry - b.daysUntilExpiry;
    });

    setAlerts(generatedAlerts);
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          text: 'text-red-900',
          badge: 'bg-red-600 text-white',
          icon: 'text-red-600',
          pulse: 'animate-pulse'
        };
      case 'high':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-300',
          text: 'text-orange-900',
          badge: 'bg-orange-600 text-white',
          icon: 'text-orange-600',
          pulse: ''
        };
      case 'medium':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-300',
          text: 'text-yellow-900',
          badge: 'bg-yellow-600 text-white',
          icon: 'text-yellow-600',
          pulse: ''
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-300',
          text: 'text-blue-900',
          badge: 'bg-blue-600 text-white',
          icon: 'text-blue-600',
          pulse: ''
        };
    }
  };

  const handleDismissAlert = (alertId) => {
    const newDismissed = [...dismissedAlerts, alertId];
    setDismissedAlerts(newDismissed);
    localStorage.setItem('dismissedAlerts', JSON.stringify(newDismissed));
  };

  const handleContactAgent = (alert) => {
    const message = `Hi, I need to renew my ${alert.policyType} (Policy #${alert.policyNumber}) which expires on ${format(new Date(alert.expirationDate), 'MMM dd, yyyy')}.`;
    window.open(`${AGENCY_PHONE_TEL}?body=${encodeURIComponent(message)}`, '_self');
  };

  const visibleAlerts = alerts.filter(alert => !dismissedAlerts.includes(alert.id));
  // Zero alerts means something different depending on whether any policy is
  // actually current -- see the empty states below.
  const activeCount = currentPolicies(user?.policies).length;
  const criticalCount = visibleAlerts.filter(a => a.severity === 'critical' || a.severity === 'high').length;

  // Compact Widget View (for Dashboard)
  if (isCompact) {
    if (visibleAlerts.length === 0) {
      // "All Policies Current" is only true if a current policy exists. With
      // none, the honest statement is that we hold no current record -- which
      // may mean our records are behind, not that the client is uninsured.
      if (activeCount === 0) {
        return (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">No current policies on record</h3>
                <p className="text-sm text-gray-600">
                  If you have active coverage with us, our records may need updating —
                  call or text us at {AGENCY_PHONE_DISPLAY}
                </p>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">All Policies Current</h3>
              <p className="text-sm text-gray-600">No expiring policies at this time</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Bell size={20} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Policy Expiration Alerts</h3>
              <p className="text-sm text-gray-600">
                {criticalCount > 0 ? `${criticalCount} urgent alert${criticalCount !== 1 ? 's' : ''}` : `${visibleAlerts.length} alert${visibleAlerts.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          {criticalCount > 0 && (
            <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
              {criticalCount} URGENT
            </span>
          )}
        </div>

        <div className="space-y-3">
          {visibleAlerts.slice(0, 3).map((alert) => {
            const styles = getSeverityStyles(alert.severity);
            return (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border-2 ${styles.border} ${styles.bg} ${styles.pulse}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-3 flex-1">
                    <AlertTriangle size={18} className={styles.icon} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm mb-1">
                        {alert.title}
                      </div>
                      <div className="text-xs text-gray-700 mb-2">
                        {alert.policyType} • {alert.vendor}
                      </div>
                      <div className={`text-xs ${styles.text} font-medium`}>
                        {alert.message}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDismissAlert(alert.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors ml-2"
                  >
                    <X size={16} />
                  </button>
                </div>

                {alert.actionRequired && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleContactAgent(alert)}
                      className="flex-1 text-xs bg-white text-gray-900 py-2 px-3 rounded font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-1 border border-gray-300"
                    >
                      <Phone size={12} />
                      Call Agent
                    </button>
                    <button
                      className="flex-1 text-xs bg-white text-gray-900 py-2 px-3 rounded font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-1 border border-gray-300"
                    >
                      <Mail size={12} />
                      Email
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {visibleAlerts.length > 3 && (
          <button
            onClick={onViewAll}
            className="w-full mt-4 text-sm text-[#DC143C] font-semibold hover:underline flex items-center justify-center gap-2"
          >
            View All {visibleAlerts.length} Alerts
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    );
  }

  // Full Page View
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{visibleAlerts.length}</div>
          <div className="text-sm text-gray-600">Total Alerts</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-red-200 bg-red-50">
          <div className="text-2xl font-bold text-red-600">
            {visibleAlerts.filter(a => a.severity === 'critical').length}
          </div>
          <div className="text-sm text-red-800">Critical</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-orange-200 bg-orange-50">
          <div className="text-2xl font-bold text-orange-600">
            {visibleAlerts.filter(a => a.severity === 'high').length}
          </div>
          <div className="text-sm text-orange-800">High Priority</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-blue-200 bg-blue-50">
          <div className="text-2xl font-bold text-blue-600">
            {visibleAlerts.filter(a => a.severity === 'medium' || a.severity === 'low').length}
          </div>
          <div className="text-sm text-blue-800">Upcoming</div>
        </div>
      </div>

      {/* Alerts List */}
      {visibleAlerts.length === 0 ? (
        activeCount === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-amber-500" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No current policies on record
            </h3>
            <p className="text-gray-600">
              If you have active coverage with us and don&apos;t see it here, call or text
              us at {AGENCY_PHONE_DISPLAY} — our records may need updating.
            </p>
          </div>
        ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            All Policies Current!
          </h3>
          <p className="text-gray-600">
            No expiring policies at this time. We'll notify you when renewals are available.
          </p>
        </div>
        )
      ) : (
        <div className="space-y-4">
          {visibleAlerts.map((alert) => {
            const styles = getSeverityStyles(alert.severity);
            return (
              <div
                key={alert.id}
                className={`bg-white rounded-xl shadow-sm border-2 ${styles.border} p-6 ${styles.pulse}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-12 h-12 ${styles.bg} rounded-lg flex items-center justify-center`}>
                      <AlertTriangle size={24} className={styles.icon} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">
                          {alert.title}
                        </h3>
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${styles.badge} uppercase`}>
                          {alert.severity}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-3">
                        {alert.policyType} • {alert.vendor} • Policy #{alert.policyNumber}
                      </div>
                      <div className={`text-sm font-medium ${styles.text} mb-4`}>
                        {alert.message}
                      </div>

                      {/* Policy Details */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Expiration Date:</span>
                            <div className="font-semibold text-gray-900 flex items-center gap-2">
                              <Calendar size={14} />
                              {format(new Date(alert.expirationDate), 'MMMM dd, yyyy')}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Days Remaining:</span>
                            <div className={`font-bold text-lg ${alert.daysUntilExpiry <= 7 ? 'text-red-600' : 'text-gray-900'}`}>
                              {alert.daysUntilExpiry}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => handleContactAgent(alert)}
                          className="flex-1 bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] transition-colors flex items-center justify-center gap-2"
                        >
                          <Phone size={18} />
                          Call to Renew Now
                        </button>
                        <a
                          href={`mailto:info@pinoygeneralinsurance.com?subject=Renewal Request - ${alert.policyNumber}`}
                          className="flex-1 border border-[#0038A8] text-[#0038A8] py-3 px-4 rounded-lg font-semibold hover:bg-[#0038A8] hover:text-white transition-colors flex items-center justify-center gap-2"
                        >
                          <Mail size={18} />
                          Email Agent
                        </a>
                        <button
                          onClick={() => handleDismissAlert(alert.id)}
                          className="sm:w-auto text-gray-600 hover:text-gray-800 font-medium py-2 px-4"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Important Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">
              Why Timely Renewal Matters
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Avoid Coverage Gaps:</strong> Protect yourself from being uninsured</li>
              <li>• <strong>Better Rates:</strong> Early renewals often qualify for better pricing</li>
              <li>• <strong>Continuous Protection:</strong> Maintain your claims history and loyalty discounts</li>
              <li>• <strong>Peace of Mind:</strong> Don't worry about last-minute renewals</li>
            </ul>
            <div className="mt-4 pt-4 border-t border-blue-300">
              <p className="text-sm text-blue-900">
                <strong>Need Help?</strong> Call us at{' '}
                <a href={AGENCY_PHONE_TEL} className="font-bold underline">
                  {AGENCY_PHONE_DISPLAY}
                </a>{' '}
                for personalized renewal assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpirationAlertsWidget;